import gsap from 'gsap';
import { Container } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';
import { sound } from '../utils/soundManager.js';
import { clearParticleLayer, spawnCoinShower, spawnParticleBurst } from './particleBurst.js';

/** Win tier → shake strength multiplier for pre-popup anticipation. */
const TIER_SHAKE_MUL = {
  big: 1,
  huge: 1.12,
  mega: 1.28,
  epic: 1.45,
};

/**
 * Pre-popup rumble length — matches {@link sound.playBigWinAlert} clip when loaded.
 * @returns {number}
 */
export function getCelebrationAnticipationSec() {
  const alertSec = sound.getDurationSec('bigWinAlert');
  if (alertSec > 0) return alertSec;
  const fx = gameConfig.visualFx ?? {};
  return Math.max(0, Number(fx.celebrationAnticipationSec) || 0);
}

/**
 * Lightweight celebration polish — glow pulse, pre-popup shake, collect particles.
 * Must NOT scale or permanently move layout nodes (backdrop cover-fit, gameVisualLayer position).
 */
export class CabinetJuice {
  /**
   * @param {{
   *   stage: import('pixi.js').Container,
   *   glowSprite?: import('pixi.js').Sprite | null,
   *   shakeTarget?: import('pixi.js').Container | null,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   *   onCelebrationStart?: () => void,
   *   onCelebrationEnd?: () => void,
   * }} opts
   */
  constructor(opts) {
    this._stage = opts.stage;
    this._glowSprite = opts.glowSprite ?? null;
    this._shakeTarget = opts.shakeTarget ?? null;
    this._animationController = opts.animationController;
    this._onCelebrationStart = opts.onCelebrationStart ?? null;
    this._onCelebrationEnd = opts.onCelebrationEnd ?? null;

    this._fxLayer = new Container();
    this._fxLayer.eventMode = 'none';
    this._fxLayer.zIndex = 99990;
    this._stage.addChild(this._fxLayer);
    if (this._stage.sortableChildren) this._stage.sortableChildren = true;

    /** @type {import('gsap').Tween | import('gsap').Timeline | null} */
    this._glowPulseTween = null;
    /** @type {import('gsap').Timeline | null} */
    this._shakeTween = null;
    /** @type {{ x: number, y: number } | null} */
    this._shakeOrigin = null;
    this._celebrationActive = false;
  }

  /**
   * @param {import('pixi.js').Sprite | null} glow
   */
  setGlowSprite(glow) {
    this._glowSprite = glow;
  }

  /**
   * @param {import('pixi.js').Container | null} target
   */
  setShakeTarget(target) {
    this._shakeTarget = target ?? null;
  }

  /**
   * Premium cabinet rumble before the first big-win popup.
   * @param {'big' | 'huge' | 'mega' | 'epic'} tier
   * @returns {Promise<void>}
   */
  playCelebrationAnticipation(tier) {
    const fx = gameConfig.visualFx ?? {};
    const target = this._shakeTarget;
    this._stopShake();
    const totalSec = getCelebrationAnticipationSec();

    if (totalSec <= 0.01) {
      return Promise.resolve();
    }

    sound.playBigWinAlert();

    if (!target) {
      return new Promise((resolve) => {
        setTimeout(resolve, totalSec * 1000);
      });
    }

    const baseIntensity = fx.celebrationShakeIntensity ?? 7;
    const tierMul = TIER_SHAKE_MUL[tier] ?? 1;
    /** Match the opening rumble — constant gentle shake for the full alert clip. */
    const intensity = baseIntensity * tierMul * 0.22;
    const ox = target.x;
    const oy = target.y;
    this._shakeOrigin = { x: ox, y: oy };

    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: () => {
          target.position.set(ox, oy);
          this._shakeTween = null;
          this._shakeOrigin = null;
          this._animationController.releaseTween(tl);
          resolve();
        },
      });

      const settleSec = Math.min(0.1, totalSec * 0.06);
      const shakeSec = Math.max(0.05, totalSec - settleSec);

      this._appendUniformShake(tl, target, ox, oy, intensity, shakeSec);
      tl.to(target, { x: ox, y: oy, duration: settleSec, ease: 'power2.out' });

      if (this._glowSprite) {
        const flashAlpha = fx.celebrationFlashAlpha ?? 0.32;
        const flashDur = fx.celebrationFlashDuration ?? 0.12;
        tl.to(
          this._glowSprite,
          { alpha: Math.min(1, flashAlpha + 0.55), duration: flashDur * 0.45, ease: 'power2.out' },
          `-=${settleSec + flashDur * 0.45}`,
        );
        tl.to(this._glowSprite, { alpha: 1, duration: flashDur * 1.1, ease: 'sine.out' });
      }

      tl.call(() => {
        spawnParticleBurst(this._fxLayer, 961, 440, {
          count: fx.particleBurstCount ?? 14,
          spread: (fx.particleBurstSpread ?? 52) + 28,
          color: gameConfig.theme?.gold ?? 0xe7c463,
        });
      }, undefined, `-=${settleSec}`);

      this._shakeTween = tl;
      this._animationController.trackTween(tl);
    });
  }

  /**
   * @param {import('gsap').Timeline} tl
   * @param {import('pixi.js').Container} target
   * @param {number} ox
   * @param {number} oy
   * @param {number} intensity
   * @param {number} duration
   */
  _appendUniformShake(tl, target, ox, oy, intensity, duration) {
    const stepSec = 0.034;
    const steps = Math.max(2, Math.round(duration / stepSec));
    const stepDur = duration / steps;

    for (let i = 0; i < steps; i += 1) {
      tl.to(target, {
        x: ox + (Math.random() - 0.5) * 2 * intensity,
        y: oy + (Math.random() - 0.5) * 2 * intensity * 0.62,
        duration: stepDur,
        ease: 'none',
      });
    }
  }

  startCelebrationMode() {
    if (this._celebrationActive) return;
    this._celebrationActive = true;
    this._onCelebrationStart?.();
    this._startGlowPulse();
  }

  stopCelebrationMode() {
    this._celebrationActive = false;
    this._stopShake();
    this._stopGlowPulse();
    this._onCelebrationEnd?.();
  }

  /**
   * Fired on each tier popup step during buildup.
   * @param {'big' | 'huge' | 'mega' | 'epic'} tier
   */
  onCelebrationTierStep(tier) {
    if (this._glowSprite) {
      gsap.killTweensOf(this._glowSprite, 'alpha');
      gsap.fromTo(
        this._glowSprite,
        { alpha: 0.85 },
        { alpha: 1, duration: 0.25, yoyo: true, repeat: 1, ease: 'sine.inOut' },
      );
    }
  }

  /** Kill in-flight collect / anticipation particles (new spin / teardown). */
  clearFxParticles() {
    clearParticleLayer(this._fxLayer);
  }

  /**
   * Collect tap — coin shower at screen center.
   */
  playCollectBurst() {
    const fx = gameConfig.visualFx ?? {};
    spawnCoinShower(this._fxLayer, 961, 420, { count: fx.collectCoinCount ?? 24 });
    spawnParticleBurst(this._fxLayer, 961, 380, {
      count: 18,
      spread: 90,
      color: gameConfig.theme?.gold ?? 0xe7c463,
    });
  }

  /** Expanding-wild reveal — no full-screen flash. */
  playExpandingWildImpact() {}

  _stopShake() {
    sound.stopBigWinAlert();
    if (!this._shakeTween) return;
    const target = this._shakeTarget;
    const origin = this._shakeOrigin;
    this._shakeTween.kill();
    this._animationController.releaseTween(this._shakeTween);
    this._shakeTween = null;
    this._shakeOrigin = null;
    if (target) {
      gsap.killTweensOf(target, 'x');
      gsap.killTweensOf(target, 'y');
      if (origin) target.position.set(origin.x, origin.y);
    }
  }

  _startGlowPulse() {
    if (!this._glowSprite) return;
    this._stopGlowPulse();
    const fx = gameConfig.visualFx ?? {};
    const base = this._glowSprite.alpha;
    this._glowPulseTween = gsap.to(this._glowSprite, {
      alpha: Math.min(1, base + (fx.celebrationGlowPulseSwing ?? 0.22)),
      duration: fx.celebrationGlowPulseSec ?? 0.9,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
    this._animationController.trackTween(this._glowPulseTween);
  }

  _stopGlowPulse() {
    if (this._glowPulseTween) {
      this._glowPulseTween.kill();
      this._animationController.releaseTween(this._glowPulseTween);
      this._glowPulseTween = null;
    }
    if (this._glowSprite) {
      gsap.killTweensOf(this._glowSprite, 'alpha');
      this._glowSprite.alpha = 1;
    }
  }

  destroy() {
    this.stopCelebrationMode();
    this.clearFxParticles();
    this._fxLayer.parent?.removeChild(this._fxLayer);
    this._fxLayer.destroy({ children: true });
  }
}
